import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { StatCard } from '../components/shared/StatCard';
import { Spinner }  from '../components/shared/Spinner';
import { ordersAPI } from '../services/api';
import { fmt }       from '../components/shared/fmt';
import { useTitle }  from '../hooks/useTitle';

export const OrdersPage: React.FC = () => {
  useTitle('Orders');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey:['orders'], queryFn: () => ordersAPI.getAll() });
  const orders = data?.data || [];
  const [fSt,    setFSt]    = useState('all');
  const [search, setSearch] = useState('');

  const voidMut = useMutation({ mutationFn: ordersAPI.void, onSuccess:()=>{ toast.success('Order voided'); qc.invalidateQueries({queryKey:['orders']}); } });

  const visible = orders.filter((o:any) => (fSt==='all'||o.status===fSt) && (!search||o.id.includes(search)||o.tableLabel?.includes(search)));
  const paidRev = orders.filter((o:any)=>o.status==='paid').reduce((s:number,o:any)=>s+o.total,0);

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="page-title">Order History</div>
      <div className="page-sub">{orders.length} total orders</div>

      <div className="stat-grid">
        <StatCard label="Total"   value={orders.length} icon="📋" />
        <StatCard label="Paid"    value={orders.filter((o:any)=>o.status==='paid').length}  cls="green" icon="✅" />
        <StatCard label="Open"    value={orders.filter((o:any)=>o.status==='open').length}  cls="amber" icon="🔓" />
        <StatCard label="Voided"  value={orders.filter((o:any)=>o.status==='void').length}  cls="red"   icon="❌" />
        <StatCard label="Revenue" value={fmt(paidRev)} cls="green" icon="💰" />
      </div>

      <div className="card">
        <div className="card-title">
          Orders
          <div className="card-actions">
            <input className="search-input" placeholder="🔍 Receipt # or table..." value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
        </div>
        <div className="filter-tabs">
          {['all','open','paid','void'].map(f => (
            <button key={f} className={`filter-tab ${fSt===f?'active':''}`} onClick={()=>setFSt(f)}>
              {f.charAt(0).toUpperCase()+f.slice(1)} ({f==='all'?orders.length:orders.filter((o:any)=>o.status===f).length})
            </button>
          ))}
        </div>
        <table className="data-table">
          <thead><tr><th>Receipt #</th><th>Table</th><th>Type</th><th>Items</th><th>Total</th><th>Time</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {visible.map((o:any) => (
              <tr key={o.id}>
                <td><span style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:12 }}>{o.id}</span></td>
                <td><span className="tag tag-blue">{o.tableLabel}</span></td>
                <td><span style={{ fontSize:11, color:'var(--text2)', textTransform:'capitalize' }}>{o.orderType?.replace('_',' ')}</span></td>
                <td style={{ fontSize:12, color:'var(--text2)', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {o.items?.map((i:any)=>`${i.emoji} ${i.name} ×${i.qty}`).join(', ')}
                </td>
                <td className="text-accent" style={{ fontWeight:700 }}>{fmt(o.total)}</td>
                <td style={{ color:'var(--muted)', fontSize:12 }}>{new Date(o.createdAt).toLocaleTimeString('en-KW',{hour:'2-digit',minute:'2-digit'})}</td>
                <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                <td>
                  <div style={{ display:'flex', gap:5 }}>
                    <button className="btn-secondary" style={{ fontSize:10 }}>🧾 Receipt</button>
                    {o.status==='open' && <button className="btn-secondary btn-danger" style={{ fontSize:10 }} onClick={()=>{ if(window.confirm('Void order?')) voidMut.mutate(o.id); }}>Void</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length===0 && <div className="empty-state"><div className="empty-icon">📋</div><div>No orders found</div></div>}
      </div>
    </div>
  );
};
